import { useState, useEffect } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertTriangle, Trash2, Loader2, ShieldAlert, RefreshCw } from 'lucide-react';
import { adminApi, championshipApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const SCOPES = [
  { key: 'matches', label: 'Lutas (matches)', desc: 'Todos os resultados e duelos gerados' },
  { key: 'brackets', label: 'Chaves (brackets)', desc: 'Estruturas de chaveamento (gera novo código sequencial após reset)' },
  { key: 'live_rankings', label: 'Ranking ao vivo', desc: 'Pontuação atual dos atletas' },
  { key: 'registrations', label: 'Inscrições', desc: 'Todos os atletas inscritos nos campeonatos' },
  { key: 'superfights', label: 'Lutas casadas (superfights)', desc: 'Combates avulsos / superlutas' },
  { key: 'medals', label: 'Medalhas', desc: 'Medalhas finalizadas' },
  { key: 'weight_change_log', label: 'Histórico de troca de peso', desc: 'Logs de mudanças de categoria' },
];

const DEFAULT_SELECTED = SCOPES.map((s) => s.key);

export default function AdminResetPage() {
  const { user } = useAuth();
  const isAuthorized = user?.role === 'super_admin';

  const [selected, setSelected] = useState(DEFAULT_SELECTED);
  const [championshipId, setChampionshipId] = useState('all');
  const [championships, setChampionships] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const handleMigrateAgeClasses = async () => {
    if (!window.confirm('Recomputar categoria de idade + classe de peso de TODOS os atletas e inscrições?\n\nEsta ação é segura e reversível — apenas recalcula rótulos com base na tabela padrão atual.')) return;
    setMigrating(true);
    try {
      const { data } = await adminApi.migrateAgeClasses();
      const r = data.report || {};
      toast.success(
        `Migração concluída · atletas alterados: ${r.athletes_changed}/${r.athletes_touched} · inscrições alteradas: ${r.registrations_changed}/${r.registrations_touched}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao rodar migração');
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized) return;
    championshipApi.list().then(({ data }) => setChampionships(data || [])).catch(() => {});
  }, [isAuthorized]);

  if (!isAuthorized) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Acesso negado</h2>
          <p className="text-[#A1A1AA]">Apenas Super Admin pode acessar esta página.</p>
        </div>
      </MainLayout>
    );
  }

  const toggleScope = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleReset = async () => {
    if (confirmText !== 'ZERAR') {
      toast.error('Digite "ZERAR" exatamente para confirmar');
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminApi.resetCompetitionData({
        scopes: selected,
        confirm: 'ZERAR',
        championship_id: championshipId === 'all' ? null : championshipId,
      });
      const lines = Object.entries(data.deleted || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(' · ');
      toast.success(`Reset concluído. ${lines}`);
      setConfirmOpen(false);
      setConfirmText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao resetar dados');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-reset-page">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Chivo'] flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-red-400" />
            Resetar Dados de Competição
          </h1>
          <p className="text-[#A1A1AA] mt-2">
            Ação <strong className="text-red-400">irreversível</strong>. Apaga registros gerados durante competições.
            Atletas, equipes, federações, categorias e usuários são <strong className="text-[#D4AF37]">preservados</strong>.
          </p>
        </div>

        <Card className="bg-[#141414] border-red-900/40">
          <CardHeader>
            <CardTitle className="text-white text-xl">1. Escopo do reset</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Aplicar a:</Label>
              <Select value={championshipId} onValueChange={setChampionshipId}>
                <SelectTrigger
                  data-testid="reset-championship-select"
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="all" className="text-white hover:bg-[#27272A]">
                    🌐 Todos os campeonatos (global)
                  </SelectItem>
                  {championships.map((c) => (
                    <SelectItem key={c._id} value={c._id} className="text-white hover:bg-[#27272A]">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {championshipId === 'all' && (
                <p className="text-xs text-amber-400">
                  ⚠ Modo global: apagará dados de TODOS os campeonatos do sistema.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <Label className="text-[#A1A1AA]">Coleções a apagar:</Label>
              {SCOPES.map((s) => (
                <label
                  key={s.key}
                  className="flex items-start gap-3 p-3 rounded-lg border border-[#27272A] hover:border-[#3F3F46] cursor-pointer transition-colors"
                  data-testid={`reset-scope-${s.key}`}
                >
                  <Checkbox
                    checked={selected.includes(s.key)}
                    onCheckedChange={() => toggleScope(s.key)}
                    className="mt-0.5 border-[#3F3F46] data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                  />
                  <div className="flex-1">
                    <p className="text-white font-semibold">{s.label}</p>
                    <p className="text-xs text-[#A1A1AA]">{s.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-red-900/40">
          <CardHeader>
            <CardTitle className="text-white text-xl">2. Executar reset</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              data-testid="reset-trigger-btn"
              variant="destructive"
              disabled={selected.length === 0}
              onClick={() => setConfirmOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Zerar {selected.length} coleção(ões)
            </Button>
            {selected.length === 0 && (
              <p className="text-xs text-[#A1A1AA] mt-2">Selecione ao menos uma coleção.</p>
            )}
          </CardContent>
        </Card>

        {/* Non-destructive maintenance utilities */}
        <Card className="bg-[#141414] border-[#D4AF37]/30">
          <CardHeader>
            <CardTitle className="text-white text-xl flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#D4AF37]" />
              Manutenção — Recomputar categorias
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[#A1A1AA]">
              Recalcula <strong className="text-white">categoria de idade</strong>, <strong className="text-white">classe de peso</strong> e <strong className="text-white">categoria de peso</strong> de todos os atletas e inscrições, usando a tabela padrão atualmente configurada em <span className="text-[#D4AF37]">/categorias</span>. Também re-vincula campeonatos cujo `category_table_id` esteja vazio ou apontando para uma tabela deletada.
            </p>
            <p className="text-xs text-[#71717A]">
              Ação <strong className="text-green-400">segura</strong> — apenas recalcula rótulos, não apaga nada. Use após atualizar/reimportar uma tabela de categorias.
            </p>
            <Button
              data-testid="migrate-age-classes-btn"
              onClick={handleMigrateAgeClasses}
              disabled={migrating}
              className="bg-[#D4AF37] hover:bg-[#E5C158] text-black font-bold"
            >
              {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Recomputar categorias agora
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!loading) { setConfirmOpen(open); setConfirmText(''); } }}>
        <DialogContent className="bg-[#141414] border-red-900/60 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirmação final
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] space-y-2 pt-2">
              <span className="block">
                Você está prestes a apagar <strong className="text-white">permanentemente</strong>{' '}
                {selected.length} coleção(ões){' '}
                {championshipId === 'all'
                  ? 'do sistema INTEIRO'
                  : `do campeonato "${championships.find((c) => c._id === championshipId)?.name || ''}"`}.
              </span>
              <span className="block text-red-400 font-semibold">
                Esta ação não pode ser desfeita.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-white">
              Digite <span className="font-mono text-red-400">ZERAR</span> para confirmar:
            </Label>
            <Input
              data-testid="reset-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ZERAR"
              autoFocus
              className="bg-[#0A0A0A] border-[#27272A] text-white font-mono"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setConfirmOpen(false); setConfirmText(''); }}
              disabled={loading}
              className="border-[#27272A] text-white hover:bg-[#27272A]"
            >
              Cancelar
            </Button>
            <Button
              data-testid="reset-confirm-btn"
              onClick={handleReset}
              disabled={loading || confirmText !== 'ZERAR'}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Confirmar reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
